import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  bulkUpdateGrantOpportunities,
  createGrantOpportunity,
  deleteGrantOpportunity,
  listGrantOpportunities,
  type GrantOpportunity,
  type GrantOpportunityEngagementType,
  type GrantOpportunityStatus,
  updateGrantOpportunity,
} from "@/api/grant_opportunities";
import {
  joinDepartment,
  leaveDepartment,
  listDepartments,
  type Department,
} from "@/api/departments";
import {
  GRANT_OPPORTUNITY_PIPELINE_STATUS_ORDER,
  sortGrantOpportunitiesByDeadline,
} from "@/components/grant_suite/grantSuiteDisplay";
import type { GrantOpportunityFocusRequest } from "@/components/grant_suite/grantSuiteNavigation";
import { useToast } from "@/hooks/useToast";
import {
  invalidateGrantOpportunityEvents,
  invalidateGrantSuitePipeline,
} from "@/lib/grantSuiteQueryKeys";

export type GrantTrackListFilter = "watchlist" | "pursuing";
export type GrantTrackStatusFilter = GrantOpportunityStatus | "all";

type UseGrantTrackOptions = {
  municipalityId?: number;
  defaultFilter?: GrantTrackListFilter;
  focusRequest?: GrantOpportunityFocusRequest | null;
  onFocusRequestHandled?: () => void;
};

export function useGrantTrack({
  municipalityId,
  defaultFilter = "watchlist",
  focusRequest = null,
  onFocusRequestHandled,
}: UseGrantTrackOptions) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [filter, setFilter] = useState<GrantTrackListFilter>(defaultFilter);
  const [statusFilter, setStatusFilter] = useState<GrantTrackStatusFilter>("all");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<GrantOpportunityStatus>("preparing");
  const [bulkDeadline, setBulkDeadline] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pendingFocusId, setPendingFocusId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<GrantOpportunity | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formUrl, setFormUrl] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formWatching, setFormWatching] = useState(true);
  const [formDeadline, setFormDeadline] = useState("");
  // ENG-1824: AI Partner entry. Off by default so the create dialog behaves
  // exactly as it always has until someone opts this opportunity in.
  const [formAiPartner, setFormAiPartner] = useState(false);
  const [formEngagementType, setFormEngagementType] =
    useState<GrantOpportunityEngagementType>("applying");
  const [formOutsideBeneficiary, setFormOutsideBeneficiary] = useState("");
  const [exportingCsv, setExportingCsv] = useState(false);
  // The patch of the most recent rejected update, re-set (a new object) on
  // every failure so a repeat rejection of the same field still registers.
  const [rejectedUpdate, setRejectedUpdate] = useState<
    Parameters<typeof updateGrantOpportunity>[2] | null
  >(null);
  // null means the team Tracker list. A department id narrows every read to
  // that department, and the two sets never overlap.
  const [departmentId, setDepartmentId] = useState<number | null>(null);

  const { data: departmentsData } = useQuery({
    queryKey: ["departments", municipalityId] as const,
    queryFn: () => listDepartments(municipalityId!),
    enabled: Boolean(municipalityId),
  });

  // Departments are optional context for the Tracker, not load-bearing. Coerce
  // rather than trust the payload so an unexpected response degrades to "no
  // departments" instead of taking the whole panel down.
  const departments: Department[] = Array.isArray(departmentsData) ? departmentsData : [];

  const departmentScope = departmentId ?? undefined;

  const allQueryKey = useMemo(
    () => ["grant_opportunities", municipalityId, "summary", departmentId] as const,
    [municipalityId, departmentId],
  );

  const { data: allOpportunities = [] } = useQuery({
    queryKey: allQueryKey,
    queryFn: () => listGrantOpportunities(municipalityId!, {departmentId: departmentScope}),
    enabled: Boolean(municipalityId),
  });

  const queryKey = useMemo(
    () => ["grant_opportunities", municipalityId, filter, departmentId] as const,
    [municipalityId, filter, departmentId],
  );

  const { data: opportunities = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      listGrantOpportunities(municipalityId!, {
        watching: filter === "watchlist",
        departmentId: departmentScope,
      }),
    enabled: Boolean(municipalityId),
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let matches = opportunities;

    if (filter === "pursuing" && statusFilter !== "all") {
      matches = matches.filter((opp) => opp.status === statusFilter);
    }

    if (term) {
      matches = matches.filter((opp) => {
        return (
          opp.title.toLowerCase().includes(term) ||
          (opp.notes ?? "").toLowerCase().includes(term)
        );
      });
    }

    return sortGrantOpportunitiesByDeadline(matches);
  }, [opportunities, search, filter, statusFilter]);

  const focusTarget = pendingFocusId
    ? opportunities.find((opp) => opp.id === pendingFocusId)
    : null;

  const selected =
    focusTarget ??
    filtered.find((opp) => opp.id === selectedId) ??
    filtered[0] ??
    null;

  useEffect(() => {
    setSelectedId(null);
    setPendingFocusId(null);
    setSearch("");
    setSelectedIds([]);
    setStatusFilter("all");
    setCreateOpen(false);
    setDeleteTarget(null);
  }, [municipalityId]);

  useEffect(() => {
    if (!focusRequest) return;

    setFilter("pursuing");
    setStatusFilter("all");
    setSearch("");
    setPendingFocusId(focusRequest.opportunityId);
  }, [focusRequest]);

  useEffect(() => {
    setSelectedIds([]);
    setStatusFilter("all");
  }, [filter]);

  useEffect(() => {
    if (!pendingFocusId) return;

    const focused = opportunities.find((opp) => opp.id === pendingFocusId);
    if (focused) {
      setSelectedId(focused.id);
      setPendingFocusId(null);
      onFocusRequestHandled?.();
      return;
    }

    if (!isLoading && filter === "pursuing") {
      setPendingFocusId(null);
      onFocusRequestHandled?.();
      toast({
        title: "Could not open that grant",
        description: "It may be on your watchlist only or no longer in your pursuit pipeline.",
        variant: "destructive",
      });
    }
  }, [pendingFocusId, opportunities, isLoading, filter, onFocusRequestHandled, toast]);

  useEffect(() => {
    if (pendingFocusId) return;

    if (filtered.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !filtered.some((opp) => opp.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId, pendingFocusId]);

  const invalidate = useCallback(() => {
    invalidateGrantSuitePipeline(queryClient, municipalityId);
  }, [municipalityId, queryClient]);

  const resetCreateForm = () => {
    setFormTitle("");
    setFormUrl("");
    setFormNotes("");
    setFormWatching(true);
    setFormDeadline("");
    setFormAiPartner(false);
    setFormEngagementType("applying");
    setFormOutsideBeneficiary("");
  };

  const createMutation = useMutation({
    mutationFn: (payload: Parameters<typeof createGrantOpportunity>[1]) =>
      createGrantOpportunity(municipalityId!, payload),
    onSuccess: (created) => {
      invalidate();
      setCreateOpen(false);
      // Routed through resetCreateForm so a field added to the form can never
      // again be cleared in one place and left stale in the other: before
      // this, the AI Partner fields survived a create and pre-filled the next
      // one, including a beneficiary name the next opportunity never had.
      resetCreateForm();
      setSelectedId(created.id);
      setFilter(created.watching ? "watchlist" : "pursuing");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Parameters<typeof updateGrantOpportunity>[2] }) =>
      updateGrantOpportunity(municipalityId!, id, patch),
    // Blur-committed field edits have no visible submit, so a silent failure
    // reads as saved; invalidate anyway so inputs re-sync to the server state,
    // and keep the refused patch for the ones that can't (their server value
    // is unchanged, so nothing else would tell them the save was refused).
    onError: (error, { patch }) => {
      toast({
        title: "Update failed",
        description: error instanceof Error ? error.message : "The change was not saved",
        variant: "destructive",
      });
      setRejectedUpdate(patch);
      invalidate();
    },
    onSuccess: (_updated, { id }) => {
      setRejectedUpdate(null);
      invalidate();
      // Every field change records an activity event, so the feed and its tab
      // badge go stale the moment a save lands.
      invalidateGrantOpportunityEvents(queryClient, municipalityId, id);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteGrantOpportunity(municipalityId!, id),
    onSuccess: () => {
      setDeleteTarget(null);
      invalidate();
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async (patch: Parameters<typeof updateGrantOpportunity>[2]) => {
      const result = await bulkUpdateGrantOpportunities(municipalityId!, selectedIds, patch);
      if (result.failed.length > 0) {
        throw new Error(`Updated ${result.updated_count} of ${selectedIds.length} opportunities`);
      }
      return result;
    },
    onError: (error) => {
      toast({
        title: "Bulk update incomplete",
        description: error instanceof Error ? error.message : "Some updates failed",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      setSelectedIds([]);
      setBulkDeadline("");
    },
    // A partial bulk update still applied — and recorded events for — every id
    // that succeeded, and it reports that as a failure. Refresh on both
    // outcomes, or the rows the server did change keep their old values and
    // invite a second run of the same edit. A bulk edit records events on
    // every opportunity it touched, so sweep the whole team rather than one
    // feed.
    onSettled: () => {
      invalidate();
      invalidateGrantOpportunityEvents(queryClient, municipalityId);
    },
  });

  const toggleSelected = (id: number, checked: boolean) => {
    setSelectedIds((current) =>
      checked ? [...current, id] : current.filter((value) => value !== id),
    );
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? filtered.map((opp) => opp.id) : []);
  };

  const submitCreate = () => {
    createMutation.mutate({
      title: formTitle.trim(),
      url: formUrl.trim() || undefined,
      notes: formNotes.trim() || undefined,
      watching: formWatching,
      status: formWatching ? "interested" : "preparing",
      deadline_at: formDeadline ? new Date(formDeadline).toISOString() : null,
      // New opportunities land in whichever scope is being viewed, so creating
      // from inside a department does not silently make a team opportunity.
      department_id: departmentId ?? undefined,
      // ENG-1824: the AI Partner fields only travel when the box is checked,
      // so a Tracker create sends exactly the payload it always did. The
      // beneficiary name means nothing unless the team is supporting someone.
      ...(formAiPartner
        ? {
            ai_partner: true,
            engagement_type: formEngagementType,
            outside_beneficiary_name:
              formEngagementType === "supporting" ? formOutsideBeneficiary.trim() : undefined,
          }
        : {}),
    });
  };

  const membershipMutation = useMutation({
    mutationFn: ({department, join}: {department: Department; join: boolean}) =>
      join
        ? joinDepartment(municipalityId!, department.id)
        : leaveDepartment(municipalityId!, department.id),
    onSuccess: (_result, {department, join}) => {
      queryClient.invalidateQueries({queryKey: ["departments", municipalityId]});
      invalidate();
      // Joining or leaving records an event on every opportunity scoped to the
      // department, so sweep the whole team's feeds.
      invalidateGrantOpportunityEvents(queryClient, municipalityId);
      // Leaving the department currently in view drops you back to the team
      // list; staying would just render an empty, inaccessible pipeline.
      if (!join && departmentId === department.id) setDepartmentId(null);
    },
    onError: (mutationError: Error) => {
      toast({title: mutationError.message, variant: "destructive"});
    },
  });

  return {
    allOpportunities,
    opportunities,
    filtered,
    selected,
    isLoading,
    error,
    filter,
    setFilter,
    statusFilter,
    setStatusFilter,
    search,
    setSearch,
    selectedIds,
    setSelectedIds,
    bulkStatus,
    setBulkStatus,
    bulkDeadline,
    setBulkDeadline,
    selectedId,
    setSelectedId,
    createOpen,
    setCreateOpen,
    deleteTarget,
    setDeleteTarget,
    formTitle,
    setFormTitle,
    formUrl,
    setFormUrl,
    formNotes,
    setFormNotes,
    formWatching,
    setFormWatching,
    formDeadline,
    setFormDeadline,
    formAiPartner,
    setFormAiPartner,
    formEngagementType,
    setFormEngagementType,
    formOutsideBeneficiary,
    setFormOutsideBeneficiary,
    exportingCsv,
    setExportingCsv,
    pipelineStatusOrder: GRANT_OPPORTUNITY_PIPELINE_STATUS_ORDER,
    departments,
    departmentId,
    setDepartmentId,
    membershipMutation,
    createMutation,
    updateMutation,
    rejectedUpdate,
    deleteMutation,
    bulkUpdateMutation,
    toggleSelected,
    toggleSelectAll,
    resetCreateForm,
    submitCreate,
  };
}
